import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  useTheme,
  Typography
} from '@mui/material';

function EnergiesTableContent({
  singleReport,
  reportType,
  month,
  subReportGroupSelection,
  heatmapOn
}) {

  console.log("singleReport in EnergiesTableContent: ", singleReport);

  const theme = useTheme();

  // Get measurements from singleReport based on selection
  const getMeasurements = () => {
    if (!singleReport || !singleReport.SubReports) return [];

    if (subReportGroupSelection && singleReport.SubReports[subReportGroupSelection]) {
      return singleReport.SubReports[subReportGroupSelection];
    } else {
      return Object.values(singleReport.SubReports).flat();
    }
  };

  const measurements = getMeasurements();

  // Calculate heatmap percentages for all measurements
  const calculateHeatmapPercentages = (measurements) => {
    // Collect all values to find min/max for percentage calculation
    const allValues = [];
    measurements.forEach(measurement => {
      Object.values(measurement.Values).forEach(value => {
        if (typeof value === 'number' && !isNaN(value)) {
          allValues.push(value);
        }
      });
    });

    if (allValues.length === 0) return measurements;

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const range = maxValue - minValue;

    // Add percentage to each measurement's values
    return measurements.map(measurement => ({
      ...measurement,
      Values: Object.entries(measurement.Values).map(([key, value]) => ({
        date: key,
        value: typeof value === 'number' ? value : 0,
        percentage: range === 0 ? 0 : (value - minValue) / range
      }))
    }));
  };

  const measurementsWithHeatmap = calculateHeatmapPercentages(measurements);

  function generateTableRows(keys, measurementsWithHeatmap) {
    // Map through devices/measurements, every device should have a row of data.
    const tableRows = measurementsWithHeatmap.map(measurement => {
      try {
        const deviceInfo = `${measurement.Label} (${measurement.HwId}) [${measurement.Unit}]`;
        const newRow = [
          // First element in the row is the device information
          { value: deviceInfo, percentage: "device" },
          // Then generate the row value objects for each key
          ...keys.map(key => {
            // Find the value and percentage for the current key from the measurement.Values array
            const valueObj = measurement.Values.find(val => val.date === key);
            return {
              value: valueObj ? valueObj.value.toFixed(1) : "0",
              percentage: valueObj ? valueObj.percentage : 0
            };
          })
        ];
        // Return new row (this adds it to tableRows)
        return newRow;
      } catch (error) {
        console.log("Failure in measurement data: ", error);
        return [];
      }
    });
    return tableRows;
  }

  // Get unique keys for all the 'dates and times' from the measurements
  const keys = Array.from(new Set(
    measurements.flatMap(measurement => Object.keys(measurement.Values))
  )).sort();

  // Format dates for display
  const formatDate = (key) => {
    // key is just a 01, 02, 03, 04 etc... If reportType === "Day" it means time of day, if reportType === "Month" it means the day of month
    if (reportType === "Day") {
      return `${key}:00`;
    } else if (reportType === "Month") {
      return `${key}.${("0" + month).slice(-2)}`;
    }
  };

  // Generate header row
  const generateHeader = (keys) => {
    const deviceColumnTitle = "Device";
    const headerRow = [
      { label: deviceColumnTitle },
      ...keys.map(key => ({ label: formatDate(key) }))
    ];
    return headerRow;
  };

  // Generate the rows
  const dataRows = generateTableRows(keys, measurementsWithHeatmap);
  const headerRow = generateHeader(keys);



  // Get device cell styling
  const getDeviceCellSx = () => ({
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[800],
    borderRight: '2px solid',
    borderRightColor: theme.palette.primary.main,
    position: 'sticky',
    left: 0,
    zIndex: 1,
    // height: "60px",
    minWidth: '200px',
    // maxWidth: '300px',
    // whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: 1,
  });

  // Get data cell styling based on value
  const getDataCellSx = (value) => {
    // 5-level heatmap
    const getHeatmapColor = (percentage) => {
      if (percentage < 0.2) {
        return theme.palette.heatmap.level1;
      } else if (percentage < 0.4) {
        // Very low: neutral, non-alarming
        return theme.palette.heatmap.level2;
      } else if (percentage < 0.6) {
        // Low-medium: amber
        return theme.palette.heatmap.level3;
      } else if (percentage < 0.8) {
        // Medium: orange
        return theme.palette.heatmap.level4;
      } else {
        return theme.palette.heatmap.level5;
      }
    };

    return {
      textAlign: 'center',
      padding: 0,
      backgroundColor: heatmapOn ? getHeatmapColor(value.percentage) : 'transparent',
      fontWeight: value.percentage > 0.8 ? 'bold' : 'normal',
      border: '1px solid',
      borderColor: theme.palette.grey[700],
      '&:hover': {
        border: `2px solid ${theme.palette.action.selected}`,
      }
    };
  };

  return (
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: theme.palette.grey[800],
        borderRadius: 2,
        maxHeight: '65vh', // Fixed height for scrolling
        overflow: 'auto', // Enable scrolling
      }}
    >
      <Table stickyHeader size="small">
        {/* HEADER ROW */}
        <TableHead >
          <TableRow >
            {headerRow.map((headerValue, index) => (
              <TableCell
                key={`header-${index}`}
                sx={{
                  backgroundColor: `${theme.palette.grey[900]} !important`,
                  color: theme.palette.common.white,
                  '&.MuiTableCell-stickyHeader': {
                    backgroundColor: `${theme.palette.grey[900]} !important`,
                  },
                  borderRight: '1px solid',
                  borderColor: theme.palette.grey[700],
                  textAlign: 'center',
                  px: 0.5,
                  py: 1,

                }}
              >
                <Typography sx={{ fontWeight: 'bold' }}>
                  {headerValue.label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {/* HEADER ROW END */}
        <TableBody>
          {dataRows.map((dataValuesForDevice, index) => (
            <TableRow
              key={`${index}rowValue`}
              sx={{
                ...(!heatmapOn && {
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover',
                  }
                }),
                '&:hover': {
                  backgroundColor: 'action.selected',
                }
              }}
            >
              {dataValuesForDevice.map((value, i) => (
                <TableCell
                  key={`${i}colValue`}
                  sx={
                    value.percentage === "device"
                      ? getDeviceCellSx()
                      : getDataCellSx(value)
                  }
                >
                  {value.percentage === "device" ? (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start'
                    }}>
                      <Box component="span" sx={{ fontSize: '0.9em', color: 'text.primary' }}>
                        {value.value}
                      </Box>
                    </Box>
                  ) : (
                    value.value
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default EnergiesTableContent;